import { useState, useEffect } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Loader2 } from "lucide-react";
import { CollapsibleOptions } from "../components/CollapsibleOptions";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from '../context/AuthContext'; // Import useAuth
import { useSearchParams } from 'react-router-dom'; // Add this import

export default function WebScraper() {
  const [searchParams] = useSearchParams();
  const [url, setUrl] = useState("");
  const [prompt, setPrompt] = useState("");
  const [multipleUrls, setMultipleUrls] = useState(false);
  const [options, setOptions] = useState({
    mainContent: true,
    htmlContent: false,
    delay: 1000,
    pagesToCrawl: 1,
    depth: 1,
    excludeTags: "",
    includeTags: ""
  });
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState([]); // initialize as an array for incremental updates
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState("markdown");
  const [isOpen, setIsOpen] = useState(false);
  const { currentUser } = useAuth(); // Destructure logout from context

  // Add this useEffect to handle URL parameter
  useEffect(() => {
    const urlParam = searchParams.get('url');
    if (urlParam) {
      setUrl(decodeURIComponent(urlParam));
    } else {
      setUrl(""); // Your default URL
    }
  }, [searchParams]);

  const handleScrape = async () => {
    setLoading(true);
    setError(null);
    setIsOpen(false)
    setOutput([]); // reset output array
    try {
      // Process comma-separated values into arrays.
      const processedExcludeTags = options.excludeTags
        ? options.excludeTags : [];
      const processedIncludeTags = options.includeTags
        ? options.includeTags : [];

      // Prepare URL array based on multipleUrls state
      const urlArr = multipleUrls
        ? url.split("\n").map((u) => u.trim()).filter(Boolean) // Split by new lines and trim
        : [url]; // Single URL in an array

      // Use the streaming endpoint for chunked response.
      const response = await fetch("http://localhost:8000/api/v1/scrape-chunked", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-api-key": "crawler"
        },
        body: JSON.stringify({
          email: currentUser.email,
          url,
          htmlContent: options.htmlContent,
          mainContent: options.mainContent,
          delay: options.delay,
          pagesToCrawl: options.pagesToCrawl,
          excludeTags: processedExcludeTags,
          includeTags: processedIncludeTags,
          depth: options.depth,
          multipleUrls,
          urlArr // Pass the URL array
        }),
      });

      if (!response.body) {
        throw new Error("ReadableStream not supported in this browser.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let isFirstChunk = true;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        console.log(buffer);
        // On the very first chunk, remove the known prefix.
        // Expected prefix: '{\n"msg": "DONE",\n"data": {\n"json": [\n'
        if (isFirstChunk) {
          const prefix = '{\n"msg": "DONE",\n"data": {\n"json": [\n';
          if (buffer.startsWith(prefix)) {
            buffer = buffer.slice(prefix.length);
          }
          isFirstChunk = false;
        }

        // Process complete JSON objects from buffer.
        let boundary = buffer.indexOf("},\n");
        while (boundary !== -1) {
          const jsonStr = buffer.slice(0, boundary + 1); // include the closing brace
          try {
            const obj = JSON.parse(jsonStr);
            // If an error object is encountered, update error and cancel further reading.
            if (obj.success === false) {
              setError(obj.error || "An unknown error occurred.");
              setOutput([]);
              reader.cancel();
              return;
            }
            // Update output incrementally.
            setOutput((prevOutput) => [...prevOutput, obj]);
          } catch (e) {
            // JSON might be partially received; break out to wait for more data.
            break;
          }
          // Remove processed part (remove the separator as well)
          buffer = buffer.slice(boundary + 2);
          boundary = buffer.indexOf("},\n");
        }
      }

      // End-of-stream: process any remaining buffer.
      // Expected suffix pattern e.g.: '\n]\n},\n"totalProcessed":'
      const suffixIndex = buffer.indexOf("\n]\n},\n\"totalProcessed\":");
      if (suffixIndex !== -1) {
        const remaining = buffer.slice(0, suffixIndex).trim();
        if (remaining) {
          try {
            const obj = JSON.parse(remaining);
            if (obj.success === false) {
              setError(obj.error || "An unknown error occurred.");
              setOutput([]);
            } else {
              setOutput((prevOutput) => [...prevOutput, obj]);
            }
          } catch (e) {
            console.error("Error parsing final JSON:", e);
          }
        }
      } else if (buffer.trim()) {
        // In case no suffix pattern is found, try to parse what's left.
        try {
          const obj = JSON.parse(buffer.trim());
          if (obj.success === false) {
            setError(obj.error || "An unknown error occurred.");
            setOutput([]);
          } else {
            setOutput((prevOutput) => [...prevOutput, obj]);
          }
        } catch (e) {
          console.error("Error parsing remaining JSON:", e);
        }
      }
      setTabValue("markdown");
    } catch (err) {
      console.error("Error during scraping:", err);
      setError(err.message);
      setOutput([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-4 max-w-3xl">
      <h1 className="text-3xl font-bold mb-6">Web Scraper</h1>

      <div className="space-y-4">
        {multipleUrls ? (
          <Textarea
            className="min-h-24 whitespace-pre-line"
            placeholder={"https://example.com \n https://example.com/about"}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        ) : (
          <Input
            placeholder="Enter website URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        )}
        <Textarea
          placeholder="Enter your prompt"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
        <div className="flex items-center space-x-2">
          <Checkbox
            id="multipleUrls"
            checked={multipleUrls}
            onCheckedChange={
              () => { setMultipleUrls(!multipleUrls); setUrl('') }
            }
          />
          <label htmlFor="multipleUrls" className="text-base">Use Multiple Urls</label>
        </div>
        <CollapsibleOptions setOptions={setOptions} options={options} isOpen={isOpen} setIsOpen={setIsOpen} />
        <Button onClick={handleScrape} disabled={loading}>
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Scraping...
            </>
          ) : (
            "Run Scraper"
          )}
        </Button>
      </div>

      {/* If an error is found, display it */}
      {error && (
        <div className="mt-6 text-red-500 font-semibold">{error}</div>
      )}

      {/* Display output cards only if no error is present */}
      {!error &&
        output &&
        output.map((item, index) => (
          <div className="mt-6" key={index}>
            <Card className="overflow-y-auto max-h-72 text-base">
              {/* Integrated header section with title and URL */}
              <div className="px-6 pt-4 pb-2 border-b border-gray-200 bg-gray-50">
                <div className="font-semibold">
                  {item.metaData?.title || "No Title"}
                </div>
                <div className="text-sm text-gray-500">
                  {item.metaData?.url || "No URL provided"}
                </div>
              </div>
              <Tabs defaultValue="markdown" value={tabValue} onValueChange={setTabValue}>
                <TabsList className="p-6 sticky top-0 w-full from-background/10 via-background/50 to-background/80 backdrop-blur-sm supports-[backdrop-filter]:bg-background/60 border-b">
                  <TabsTrigger value="markdown">Markdown</TabsTrigger>
                  <TabsTrigger value="json">JSON</TabsTrigger>
                  {options.htmlContent && (
                    <TabsTrigger value="html">HTML</TabsTrigger>
                  )}
                </TabsList>
                <TabsContent value="markdown">
                  <pre className="whitespace-pre-wrap text-left p-4">
                    {item.markdownData}
                  </pre>
                </TabsContent>
                <TabsContent value="json">
                  <pre className="whitespace-pre-wrap text-left p-4">
                    {JSON.stringify(item, null, 2)}
                  </pre>
                </TabsContent>
                {options.htmlContent && (
                  <TabsContent value="html">
                    {item.htmlContent && item.htmlContent.length > 0 ? (
                      <pre className="whitespace-pre-wrap text-left p-4">
                        {item.htmlContent}
                      </pre>
                    ) : (
                      <div className="text-left text-gray-500 p-2">
                        No HTML content available. Please try again.
                      </div>
                    )}
                  </TabsContent>
                )}
              </Tabs>
            </Card>
          </div>
        ))}
    </div>
  );
}